import { useReducer, createContext, useLayoutEffect, useState, useEffect } from 'react'
import { store, stateRef, subscribe, updateStateAndEmit, updateDispatchFn } from '../../framework/store'
import type { Reducer } from 'react'
import type { StoreState, Action, Selector, EqualityFn } from '../../framework/stateTypes'
import type { SparkySettings } from '../../framework/microAppTypes'

export const useStoreReducer = (reducer: Reducer<StoreState, Action>, initState: SparkySettings, initializer: (initState: SparkySettings) => StoreState): StoreState => {
  const [state, dispatchFn] = useReducer(
    (state: StoreState, action: Action) => {
      const newState = reducer(state, action)
      return updateStateAndEmit(newState)
    },
    initState,
    initializer,
  )
  useLayoutEffect(() => {
    updateDispatchFn(dispatchFn)
  }, [])
  return state
}

export const useStoreSelector = (selector: Selector, equalityFn: EqualityFn = (a, b) => Object.is(a, b)) => {
  const [value, setValue] = useState(selector(stateRef.current))

  useEffect(() => subscribe((newState) => {
    const newValue = selector(newState)
    if (!equalityFn(value, newValue)) {
      setValue(newValue)
    }
  }), [selector, equalityFn, value])

  return value
}

export const useGlobalStateRef = () => stateRef
export const useGlobalStatePassively = () => stateRef.current

export const GlobalStateContext = createContext(store)
